import { Injectable } from '@angular/core';
import { type Context, datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs';

import { LoggerName } from '@ninety/mobile/platform/api';

import { DATADOG_DEFAULT_LOGGER_CONF } from './datadog-constants';
import { DatadogLogger } from './datadog-logger';
import { DatadogLoggerFactory } from './datadog-logger-factory';
import { DatadogRumAccount, DatadogRumUser } from './datadog-rum.service';

/**
 * A wrapper around the datadogLogs library.
 *
 * Features:
 *  - Initializes the library
 *  - Create/Manage Global Context
 *  - Create/Manage Loggers
 */
@Injectable()
export class DatadogLogsService {
  get logger() {
    return datadogLogs.logger;
  }

  init(initConfiguration: LogsInitConfiguration) {
    datadogLogs.init(initConfiguration);
    return this;
  }

  /** Log an error to the default (AppModule) logger. Pass an `Error` for stack/kind capture. */
  error(message: string, context?: object, error?: Error) {
    datadogLogs.logger.error(message, context, error);
  }

  /** Log an informational breadcrumb to the default (AppModule) logger. Pass an `Error` for stack/kind capture. */
  info(message: string, context?: object, error?: Error) {
    datadogLogs.logger.info(message, context, error);
  }

  /** Log a warning to the default (AppModule) logger. Pass an `Error` for stack/kind capture. */
  warn(message: string, context?: object, error?: Error) {
    datadogLogs.logger.warn(message, context, error);
  }

  getGlobalContext() {
    return datadogLogs.getGlobalContext();
  }

  setGlobalContext(newContext: Context) {
    datadogLogs.setGlobalContext(newContext);
  }

  setGlobalContextProperty(key: string, property: unknown) {
    datadogLogs.setGlobalContextProperty(key, property);
  }

  removeGlobalContextProperty(key: string) {
    datadogLogs.removeGlobalContextProperty(key);
  }

  clearGlobalContext() {
    datadogLogs.clearGlobalContext();
  }

  setUser(user: DatadogRumUser) {
    datadogLogs.setUser(user);
  }

  clearUser() {
    datadogLogs.clearUser();
  }

  setAccount(account: DatadogRumAccount) {
    datadogLogs.setAccount(account);
  }

  clearAccount() {
    datadogLogs.clearAccount();
  }

  createLogger(name: string, conf = DATADOG_DEFAULT_LOGGER_CONF): DatadogLogger {
    return DatadogLoggerFactory(name, conf);
  }

  getLogger(name: LoggerName): DatadogLogger {
    let ddLogger = datadogLogs.getLogger(name);

    if (!ddLogger) {
      // Fallback to root logger
      ddLogger = datadogLogs.getLogger(LoggerName.AppModule);
    }

    if (!ddLogger) {
      throw new Error(`Failed to get logger for ${name}`);
    }

    return new DatadogLogger(ddLogger);
  }

  getInitConfiguration() {
    return datadogLogs.getInitConfiguration();
  }

  getInternalContext(startTime?: number) {
    return datadogLogs.getInternalContext(startTime);
  }

  onReady(callback: () => void): void {
    datadogLogs.onReady(callback);
  }
}
